import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CellContext } from '@tanstack/table-core';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { faCopy, faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteEngageMessage, duplicateEngageMessage, unscheduleEngageMessage } from 'api/engage';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { MessageStatus as StatusFilters } from 'constants/messages';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { EngageMessage } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ActionColumnProps {
    info: CellContext<EngageMessage, unknown>;
}

const ActionColumn = ({ info }: ActionColumnProps) => {
    const message = info.row.original;
    const history = useHistory();
    const match = useRouteMatch();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { error, success } = useToast();
    const { messageIndexTab } = useLocalStorage(state => state);

    const isDraft = messageIndexTab === StatusFilters.DRAFT;
    const isOutbox = messageIndexTab === StatusFilters.QUEUED;

    const { mutate: duplicateMessage } = useMutation({
        mutationFn: (messageId: number) => duplicateEngageMessage(accountId, messageId),
        onSuccess: message => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
            history.push(`/accounts/${accountId}/engage/${message.channel}/${message.id}/edit`);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to duplicate message');
        },
    });

    const { mutate: deleteDraft } = useMutation({
        mutationFn: (messageId: number) => deleteEngageMessage(accountId, messageId),
        onSuccess: () => {
            success('Message deleted successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to delete message');
        },
    });

    const { mutate: unscheduleMessage } = useMutation({
        mutationFn: (messageId: number) => unscheduleEngageMessage(accountId, messageId),
        onSuccess: () => {
            success('Message unscheduled successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to unschedule message');
        },
    });

    return (
        <DropdownMenu.Root key={message.id}>
            <Table.TableRowDropdownTrigger />
            <DropdownMenu.Content onSelect={e => e.preventDefault()} sideOffset={5} align={'end'}>
                {!isDraft && (
                    <DropdownMenu.LinkItem to={`${match.url}/${message.id}/details/edit`}>
                        <div className={'flex items-center gap-2'}>
                            <FontAwesomeIcon icon={faEdit} />
                            <p>Edit name</p>
                        </div>
                    </DropdownMenu.LinkItem>
                )}
                {isDraft && (
                    <DropdownMenu.Item
                        onClick={() => history.push(`${match.url}/${message.id}/edit`)}
                        className={'flex items-center gap-2'}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                        <p>Edit</p>
                    </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                    onClick={() => duplicateMessage(message.id)}
                    className={'flex items-center gap-2'}
                >
                    <FontAwesomeIcon icon={faCopy} />
                    <p>Duplicate</p>
                </DropdownMenu.Item>
                {isDraft && (
                    <AlertDialog.Root type={'error'}>
                        <AlertDialog.Trigger asChild={true}>
                            <DropdownMenu.Item
                                onSelect={e => e.preventDefault()}
                                className={'flex items-center gap-2'}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                <p>Delete</p>
                            </DropdownMenu.Item>
                        </AlertDialog.Trigger>
                        <AlertDialog.Portal>
                            <AlertDialog.Overlay />
                            <AlertDialog.Content>
                                <AlertDialog.Header>
                                    <AlertDialog.Icon />
                                    <AlertDialog.Close />
                                </AlertDialog.Header>
                                <AlertDialog.Body>
                                    <AlertDialog.Title>Delete draft</AlertDialog.Title>
                                    <AlertDialog.Description>
                                        You will not be able to undo this action.
                                    </AlertDialog.Description>
                                </AlertDialog.Body>
                                <AlertDialog.Footer>
                                    <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                    <AlertDialog.Action onClick={() => deleteDraft(message.id)}>
                                        Delete
                                    </AlertDialog.Action>
                                </AlertDialog.Footer>
                            </AlertDialog.Content>
                        </AlertDialog.Portal>
                    </AlertDialog.Root>
                )}
                {isOutbox && (
                    <AlertDialog.Root type="warning">
                        <AlertDialog.Trigger asChild={true}>
                            <DropdownMenu.Item
                                className={'flex items-center gap-2'}
                                onSelect={e => e.preventDefault()}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                <p>Unschedule</p>
                            </DropdownMenu.Item>
                        </AlertDialog.Trigger>
                        <AlertDialog.Portal>
                            <AlertDialog.Overlay />
                            <AlertDialog.Content>
                                <AlertDialog.Header>
                                    <AlertDialog.Icon />
                                    <AlertDialog.Close />
                                </AlertDialog.Header>
                                <AlertDialog.Body>
                                    <AlertDialog.Title>Unschedule message</AlertDialog.Title>
                                    <AlertDialog.Description>
                                        You will not be able to undo this action.
                                    </AlertDialog.Description>
                                </AlertDialog.Body>
                                <AlertDialog.Footer>
                                    <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                    <AlertDialog.Action
                                        onClick={() => unscheduleMessage(message.id)}
                                    >
                                        Unschedule
                                    </AlertDialog.Action>
                                </AlertDialog.Footer>
                            </AlertDialog.Content>
                        </AlertDialog.Portal>
                    </AlertDialog.Root>
                )}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

export { ActionColumn };
